import React from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import Layout from "../components/layouts/Layout";
import BackLink from "../components/ui/BackLink";
import LoadingErrorState from "../components/ui/LoadingErrorState";
import { RootState } from "../redux/store";
import { useStorDetailsByAdmin } from "../hooks/admin/store.admin";
import { useStoreDetailsByOwner } from "../hooks/owner/useOwnerStoreDetails";
import { useSingleStoreAction } from "../hooks/store/store";
import { supportsBookingFeatures, supportsOrderFeatures, supportsLaundryFeatures, supportsPosFeatures } from "../utils/storeMode";

interface Props {
    children: React.ReactNode;
    requireOrderFeatures?: boolean;
    requireBookingFeatures?: boolean;
    requireLaundryFeatures?: boolean;
    requirePosFeatures?: boolean;
}

export default function StoreModeGuard({
    children,
    requireOrderFeatures = false,
    requireBookingFeatures = false,
    requireLaundryFeatures = false,
    requirePosFeatures = false,
}: Props) {
    const { storeId } = useParams<{ storeId: string }>();
    const role = useSelector((state: RootState) => state.auth.role);
    const numericStoreId = storeId ? parseInt(storeId) : 0;

    const adminQuery = useStorDetailsByAdmin(numericStoreId, role === "ADMIN");
    const ownerQuery = useStoreDetailsByOwner(numericStoreId, role === "OWNER" || role === "ORGANIZER");
    const storeQuery = useSingleStoreAction(undefined, role === "STORE");

    const activeQuery = role === "ADMIN"
        ? adminQuery
        : role === "OWNER" || role === "ORGANIZER"
            ? ownerQuery
            : storeQuery;

    const mode = role === "ADMIN"
        ? adminQuery.data?.modes
        : role === "OWNER" || role === "ORGANIZER"
            ? ownerQuery.data?.modes
            : storeQuery.data?.modes;

    const isPosEnabled = role === "ADMIN"
        ? adminQuery.data?.isPosEnabled
        : role === "OWNER" || role === "ORGANIZER"
            ? ownerQuery.data?.isPosEnabled
            : storeQuery.data?.isPosEnabled;

    const fallbackPath = role === "ADMIN"
        ? numericStoreId ? `/admin/stores/${numericStoreId}` : "/admin/stores"
        : role === "OWNER"
            ? numericStoreId ? `/owner/stores/${numericStoreId}` : "/owner/stores"
            : role === "ORGANIZER"
                ? numericStoreId ? `/organizer/stores/${numericStoreId}` : "/organizer"
                : "/store/dashboard";

    if (activeQuery.isLoading) {
        return (
            <Layout title="Store Details" headline="">
                <LoadingErrorState isLoading={true} isError={false} />
            </Layout>
        );
    }

    if (activeQuery.isError) {
        return (
            <Layout title="Store Details" headline="">
                <LoadingErrorState isLoading={false} isError={true} />
            </Layout>
        );
    }

    if (requireOrderFeatures && !supportsOrderFeatures(mode || ["order"])) {
        return (
            <Layout title="Store Details" headline="">
                <div className="rounded-2xl border border-amber-200 bg-amber-50 p-6 text-center shadow-sm">
                    <h2 className="text-xl font-semibold text-Black">Feature unavailable for this store</h2>
                    <p className="mt-2 text-DarkGrey3">
                        This feature is not supported for stores using booking mode.
                    </p>
                    <div className="mt-4 flex justify-center">
                        <BackLink path={fallbackPath} />
                    </div>
                </div>
            </Layout>
        );
    }

    if (requireBookingFeatures && !supportsBookingFeatures(mode || ["order"])) {
        return (
            <Layout title="Store Details" headline="">
                <div className="rounded-2xl border border-amber-200 bg-amber-50 p-6 text-center shadow-sm">
                    <h2 className="text-xl font-semibold text-Black">Feature unavailable for this store</h2>
                    <p className="mt-2 text-DarkGrey3">
                        This feature is available only for stores using booking mode or both modes.
                    </p>
                    <div className="mt-4 flex justify-center">
                        <BackLink path={fallbackPath} />
                    </div>
                </div>
            </Layout>
        );
    }

    if (requireLaundryFeatures && !supportsLaundryFeatures(mode || ["order"])) {
        return (
            <Layout title="Store Details" headline="">
                <div className="rounded-2xl border border-amber-200 bg-amber-50 p-6 text-center shadow-sm">
                    <h2 className="text-xl font-semibold text-Black">Feature unavailable for this store</h2>
                    <p className="mt-2 text-DarkGrey3">
                        This feature is available only for stores using laundry mode.
                    </p>
                    <div className="mt-4 flex justify-center">
                        <BackLink path={fallbackPath} />
                    </div>
                </div>
            </Layout>
        );
    }

    if (requirePosFeatures && !supportsPosFeatures(isPosEnabled)) {
        return (
            <Layout title="Store Details" headline="">
                <div className="rounded-2xl border border-amber-200 bg-amber-50 p-6 text-center shadow-sm">
                    <h2 className="text-xl font-semibold text-Black">Feature unavailable for this store</h2>
                    <p className="mt-2 text-DarkGrey3">
                        POS is not enabled for this store.
                    </p>
                    <div className="mt-4 flex justify-center">
                        <BackLink path={fallbackPath} />
                    </div>
                </div>
            </Layout>
        );
    }

    return <>{children}</>;
}